import { EventEmitter } from 'eventemitter3';
import { Timekeeper } from './Timekeeper';
import { Track } from './Track';
export class VideoFrameExtractor {
    LOG_PREFIX = '[VideoFrameExtractor]';
    emitter = new EventEmitter();
    track;
    consumer;
    trackProcessor;
    abortController = new AbortController();
    stopped = false;
    outputTrack;
    // Metadata
    frameRate = 0;
    frameNumber = 0n;
    frameTimestampMicros = 0n;
    frameDurationMicros = 0n;
    isScreenSharing = false;
    rotation = 0;
    trackId;
    timestampOffsetMicros;
    constructor(track) {
        this.track = track;
        this.initializeMetadataFromTrack(track);
        this.outputTrack = new Track(track.id, true);
    }
    start = () => {
        this.consumer = new WritableStream({
            write: (frame) => {
                const unencodedVideoFrame = this.createUnencodedVideoFrame(frame);
                if (this.outputTrack.hasListener()) {
                    this.outputTrack?.emit(unencodedVideoFrame, false, [
                        unencodedVideoFrame.videoFrame,
                    ]);
                }
                else {
                    unencodedVideoFrame.videoFrame.close();
                }
            },
        });
        this.trackProcessor = new MediaStreamTrackProcessor({
            track: this.track,
        });
        this.trackProcessor.readable
            .pipeTo(this.consumer, {
            signal: this.abortController.signal,
        })
            .catch((_e) => {
            console.log(`${this.LOG_PREFIX} pipeTo canceled`, {
                trackId: this.trackId,
            });
        })
            .finally(() => {
            this.stopped = true;
            console.log(`${this.LOG_PREFIX} pipeline stopped, emitting done`, {
                trackId: this.trackId,
            });
            this.outputTrack?.emit(undefined, true);
            this.emitter.emit('aborted');
        });
    };
    stop = () => {
        console.log(`${this.LOG_PREFIX} stop called`, {
            trackId: this.trackId,
        });
        if (this.stopped) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            this.abortController.abort('Aborted');
            this.emitter.once('aborted', () => {
                resolve();
            });
        });
    };
    getOutputTrack() {
        return this.outputTrack;
    }
    /**
     * Initialize metadata from the MediaStreamTrack settings.
     */
    initializeMetadataFromTrack(track) {
        this.trackId = track.id;
        this.frameRate = track.getSettings().frameRate ?? 30;
        this.frameDurationMicros = BigInt(Math.round(1_000_000 / this.frameRate));
        this.isScreenSharing = track.getSettings().displaySurface === 'monitor';
    }
    /**
     * Create an UnencodedVideoFrame with all metadata.
     * This method is shared across all implementations.
     */
    createUnencodedVideoFrame(videoFrame) {
        if (!this.timestampOffsetMicros) {
            this.timestampOffsetMicros =
                Timekeeper.getInstance().getLastTimestampMicros();
        }
        const timestampMicros = this.frameTimestampMicros + this.timestampOffsetMicros;
        const unencodedVideoFrame = {
            frameNumber: this.frameNumber++,
            timestamp: timestampMicros,
            duration: this.frameDurationMicros,
            trackId: this.trackId ?? '',
            codedWidth: videoFrame.codedWidth,
            codedHeight: videoFrame.codedHeight,
            isScreenSharing: this.isScreenSharing,
            rotation: this.rotation,
            videoFrame: videoFrame,
            done: false,
            isKeyFrame: true,
        };
        Timekeeper.getInstance().setLastTimestampMicros(timestampMicros);
        this.frameTimestampMicros += this.frameDurationMicros;
        return unencodedVideoFrame;
    }
}
